import { Button, ButtonGroup, Select } from '@chakra-ui/react';
import { Container } from 'components/Container';
import { ContainerHead } from 'components/ContainerHead';
import { DataTable } from 'components/DataTable/DataTable';
import { orderTable } from 'components/DataTable/DataTableModels';
import { Pagination } from 'components/Pagination';
import { useApi } from 'lib/useApi';
import { Order, PaginatedResult } from 'models/ApiModels';
import { PortalContext } from 'PortalContext';
import React from 'react';

type OrderFilter = 'all' | 'terminated' | 'active';

export function OrdersPage(): JSX.Element {
  const [page, setPage] = React.useState(1);
  const [orderFilter, setOrderFilter] = React.useState<OrderFilter>('all');
  const [searchInputQuery, setSearchInputQuery] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedPageSize, setSelectedPageSize] = React.useState(50);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearchQuery(searchInputQuery);
    }, 400);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchInputQuery]);

  const { data: ordersResp, state: respState } = useApi<PaginatedResult<Order>>(
    `/Orders?page=${page}&pageSize=${selectedPageSize}&orderFilter=${orderFilter}&searchQuery=${encodeURIComponent(searchQuery)}`,
  );
  const { dispatch } = React.useContext(PortalContext);

  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_STATE', payload: respState });
  }, [dispatch, respState]);

  const items = ordersResp?.items ?? ordersResp?.Items ?? [];
  const totalItems = ordersResp?.totalItems ?? ordersResp?.TotalItems ?? 0;
  const pageSize = ordersResp?.pageSize ?? ordersResp?.PageSize ?? 50;
  const currentPage = ordersResp?.page ?? ordersResp?.Page ?? page;

  const onPageChange = React.useCallback(
    (nextPage: number): void => {
      if (nextPage < 1 || nextPage === page) {
        return;
      }
      setPage(nextPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [page],
  );

  const onFilterChange = React.useCallback(
    (nextFilter: OrderFilter): void => {
      if (nextFilter === orderFilter) {
        return;
      }

      setOrderFilter(nextFilter);
      setPage(1);
    },
    [orderFilter],
  );

  const onSearchChange = React.useCallback(
    (nextQuery: string): void => {
      setSearchInputQuery((prevQuery) => {
        if (prevQuery === nextQuery) {
          return prevQuery;
        }

        if (page !== 1) {
          setPage(1);
        }

        return nextQuery;
      });
    },
    [page],
  );

  const onPageSizeChange = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>): void => {
      setSelectedPageSize(Number(event.target.value));
      setPage(1);
    },
    [],
  );

  const orderFilterButtons = (
    <>
      <ButtonGroup size="sm" variant="outline" isAttached>
        <Button
          onClick={() => onFilterChange('all')}
          borderWidth="1px"
          bg={orderFilter === 'all' ? '#F27039' : 'white'}
          borderColor="gray.200"
          color={orderFilter === 'all' ? 'white' : 'gray.800'}
          _hover={{ bg: '#F27039', borderColor: 'gray.200', color: 'white' }}>
          Alla
        </Button>
        <Button
          onClick={() => onFilterChange('active')}
          borderWidth="1px"
          bg={orderFilter === 'active' ? '#F27039' : 'white'}
          borderColor="gray.200"
          color={orderFilter === 'active' ? 'white' : 'gray.800'}
          _hover={{ bg: '#F27039', borderColor: 'gray.200', color: 'white' }}>
          Aktiva
        </Button>
        <Button
          onClick={() => onFilterChange('terminated')}
          borderWidth="1px"
          bg={orderFilter === 'terminated' ? '#F27039' : 'white'}
          borderColor="gray.200"
          color={orderFilter === 'terminated' ? 'white' : 'gray.800'}
          _hover={{ bg: '#F27039', borderColor: 'gray.200', color: 'white' }}>
          Avslutade
        </Button>
      </ButtonGroup>
      <Select
        aria-label="Antal ordrar per sida"
        size="sm"
        value={selectedPageSize}
        onChange={onPageSizeChange}
        width="100px"
        ml={4}>
        <option value={50}>50</option>
        <option value={100}>100</option>
        <option value={1000}>1000</option>
        <option value={0}>Alla</option>
      </Select>
    </>
  );

  return (
    <>
      <ContainerHead title="Ordrar" children="" />
      <Container>
        <DataTable
          tableDef={orderTable}
          data={items}
          totalItems={totalItems}
          searchableFields={['name', 'orderReference', 'unit.externalId']}
          headerActions={orderFilterButtons}
          onSearchChange={onSearchChange}
        />
        {respState === 'success' && !items.length && <p>Ingen data</p>}
        {selectedPageSize !== 0 && (
          <Pagination
            page={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        )}
      </Container>
    </>
  );
}
